import React from "react";

interface CollapsibleFormSectionProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  id?: string;
  defaultOpen?: boolean;
  // Fires with the new open state whenever the section is expanded/collapsed.
  onToggle?: (open: boolean) => void;
}

export const CollapsibleFormSection = React.forwardRef<
  HTMLDetailsElement,
  CollapsibleFormSectionProps
>(({ title, children, className, id, defaultOpen = false, onToggle }, ref) => {
  if (!title) {
    return (
      <section
        id={id}
        className={`form-section${className ? ` ${className}` : ""}`}
      >
        {children}
      </section>
    );
  }

  return (
    <details
      ref={ref}
      id={id}
      className={`form-section collapsible-form-section${
        className ? ` ${className}` : ""
      }`}
      open={defaultOpen || undefined}
      onToggle={
        onToggle
          ? (e) => {
              // The `toggle` event bubbles, so a nested <details> inside
              // `children` would otherwise fire this parent handler. Only
              // respond to toggles from this <details> itself.
              if (e.target === e.currentTarget) {
                onToggle((e.currentTarget as HTMLDetailsElement).open);
              }
            }
          : undefined
      }
    >
      <summary className="collapsible-form-section__summary">
        <h2 className="section-title collapsible-form-section__title">
          {title}
        </h2>
        <span className="collapsible-form-section__icon" aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </summary>
      <div className="collapsible-form-section__content">{children}</div>
    </details>
  );
});

CollapsibleFormSection.displayName = "CollapsibleFormSection";
